import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import SongHero from '@/components/songs/SongHero';
import SequenceGrid from '@/components/sequences/SequenceGrid';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

interface Song {
  id: string;
  title: string;
  artist: string | null;
  album: string | null;
  slug: string;
  spotify_track_id: string | null;
  spotify_preview_url: string | null;
  spotify_duration_ms: number | null;
  spotify_popularity: number | null;
  spotify_explicit: boolean;
  album_cover_url: string | null;
  sequence_count: number;
  total_downloads: number;
  average_rating: number;
  created_at: string;
  updated_at: string;
}

interface Sequence {
  id: string;
  title: string;
  price: number;
  is_free: boolean;
  download_count: number;
  video_url: string | null;
  image_url: string | null;
  user_id: string;
  featured: boolean;
  created_at: string;
  software_type: string;
  holiday_type: string | null;
}

export default function SongDetail() {
  const { slug } = useParams<{ slug: string }>();

  // Fetch song details
  const { data: song, isLoading: songLoading, error: songError } = useQuery({
    queryKey: ['song', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('songs')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data as Song;
    },
    enabled: !!slug,
  });

  // Fetch sequences for this song
  const { data: sequences = [], isLoading: sequencesLoading } = useQuery({
    queryKey: ['song-sequences', song?.id],
    queryFn: async () => {
      if (!song?.id) return [];

      const { data, error } = await supabase
        .from('sequences')
        .select('*')
        .eq('song_id', song.id)
        .order('download_count', { ascending: false });

      if (error) throw error;
      return data as Sequence[];
    },
    enabled: !!song?.id,
  });

  // Group sequences by software type
  const xLightsSequences = sequences.filter(seq => seq.software_type === 'xLights');
  const lorSequences = sequences.filter(seq => seq.software_type === 'Light-O-Rama' || seq.software_type === 'LOR');
  
  // Determine default tab based on available sequences
  const defaultTab = xLightsSequences.length > 0 ? 'xlights' : lorSequences.length > 0 ? 'lor' : 'xlights';

  const isLoading = songLoading || sequencesLoading;

  if (songError) {
    return (
      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="flex-1">
          <div className="container mx-auto px-4 py-8">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {songError.message === 'JSON object requested, single row expected' 
                  ? 'Song not found' 
                  : 'Failed to load song details'}
              </AlertDescription>
            </Alert>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{song ? `${song.title}${song.artist ? ` - ${song.artist}` : ''} | Light Hunt` : 'Loading...'}</title>
        {song && (
          <>
            <meta name="description" content={`Download ${sequences.length} light show sequences for "${song.title}"${song.artist ? ` by ${song.artist}` : ''}. Find xLights, Vixen, and other formats.`} />
            <meta property="og:title" content={`${song.title}${song.artist ? ` - ${song.artist}` : ''}`} />
            <meta property="og:description" content={`${sequences.length} light show sequences available`} />
            {song.album_cover_url && <meta property="og:image" content={song.album_cover_url} />}
          </>
        )}
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="flex-1 bg-background">
        {isLoading ? (
          <div className="container mx-auto px-4 py-8 space-y-8">
            {/* Loading skeleton for hero */}
            <div className="space-y-4">
              <Skeleton className="h-80 w-full rounded-lg" />
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
            
            {/* Loading skeleton for sequences */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="h-64 rounded-lg" />
              ))}
            </div>
          </div>
        ) : song ? (
          <>
            <SongHero song={song} sequenceCount={sequences.length} />
            
            <div className="container mx-auto px-4 py-8">
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-2">
                  Available Sequences ({sequences.length})
                </h2>
                <p className="text-muted-foreground">
                  Choose from sequences for different light show software platforms
                </p>
              </div>

              {sequences.length > 0 ? (
                <Tabs defaultValue={defaultTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-8">
                    <TabsTrigger value="xlights" className="flex items-center gap-2">
                      xLights
                      <Badge variant="secondary" className="ml-2">
                        {xLightsSequences.length}
                      </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="lor" className="flex items-center gap-2">
                      Light-O-Rama
                      <Badge variant="secondary" className="ml-2">
                        {lorSequences.length}
                      </Badge>
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="xlights" className="space-y-6">
                    {xLightsSequences.length > 0 ? (
                      <>
                        <div className="flex items-center justify-between">
                          <h3 className="text-xl font-semibold">
                            xLights Sequences ({xLightsSequences.length})
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Compatible with xLights sequencing software
                          </p>
                        </div>
                        <SequenceGrid sequences={xLightsSequences} />
                      </>
                    ) : (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          No xLights sequences available for this song yet. Check the Light-O-Rama tab or check back later!
                        </AlertDescription>
                      </Alert>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="lor" className="space-y-6">
                    {lorSequences.length > 0 ? (
                      <>
                        <div className="flex items-center justify-between">
                          <h3 className="text-xl font-semibold">
                            Light-O-Rama Sequences ({lorSequences.length})
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Compatible with Light-O-Rama sequencing software
                          </p>
                        </div>
                        <SequenceGrid sequences={lorSequences} />
                      </>
                    ) : (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          No Light-O-Rama sequences available for this song yet. Check the xLights tab or check back later!
                        </AlertDescription>
                      </Alert>
                    )}
                  </TabsContent>
                </Tabs>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No sequences available for this song yet. Check back later!
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </>
        ) : null}
        </main>
        <Footer />
      </div>
    </>
  );
}